import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  ListItemIcon,
  Typography,
  Divider,
  Button,
  Tooltip,
  Popover,
  MenuItem,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Stack,
} from "@mui/material";
import ColumnFilter from "components/ColumnFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import {
  DataGrid,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import ExcelJS from "exceljs";
import CEQuickFilter from "components/CEQuickFilter";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Grid from "@mui/material/Grid2";
import { OVERLAY_NAMES } from "constants/Overlays";
import Menu from "@mui/material/Menu";
import { EditRounded } from "@mui/icons-material";
import FileCopyRoundedIcon from "@mui/icons-material/FileCopyRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import {
  normalizePhoneNumber,
  numericComparator,
} from "helpers/ServiceHelpers";
import api from "services/api";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import { NAVBAR_HEIGHT } from "constants/layout";

const normalizeRequirementValue = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const normalizeLinguistRequirements = (linguist) => {
  if (!linguist) return linguist;

  const requirementMap = { ...(linguist.RequirementMap || {}) };
  const requirementValues = linguist.Requirements || linguist.RequirementValues || [];

  requirementValues.forEach((requirement) => {
    const sourceColumn = requirement?.Definition?.SourceColumn;
    if (sourceColumn) {
      requirementMap[sourceColumn] = normalizeRequirementValue(requirement.Value);
    }
  });

  const normalized = {
    ...linguist,
    RequirementMap: requirementMap,
  };

  Object.entries(requirementMap).forEach(([field, fieldValue]) => {
    normalized[field] = normalizeRequirementValue(fieldValue);
  });

  return normalized;
};

const HOURS_IN_DAY = 24;
const MS_IN_HOUR = 60 * 60 * 1000;
const MS_IN_DAY = HOURS_IN_DAY * MS_IN_HOUR;

const parseDateOnlyToLocalDate = (dateValue) => {
  if (!dateValue || typeof dateValue !== "string") {
    return null;
  }

  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const getElapsedCalendarParts = (startDate, endDate) => {
  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  let days = endDate.getDate() - startDate.getDate();

  if (days < 0) {
    months -= 1;
    const previousMonthLastDay = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      0,
    ).getDate();
    days += previousMonthLastDay;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
  };
};

const formatLastAssignmentAge = (dateValue) => {
  const assignmentDate = parseDateOnlyToLocalDate(dateValue);
  if (!assignmentDate) {
    return "Never";
  }

  const now = new Date();
  const diffMs = now.getTime() - assignmentDate.getTime();

  if (diffMs <= 0) {
    return "0h";
  }

  if (diffMs < MS_IN_DAY) {
    const hours = Math.max(1, Math.floor(diffMs / MS_IN_HOUR));
    return `${hours}h`;
  }

  const days = Math.max(1, Math.floor(diffMs / MS_IN_DAY));

  if (days > 365) {
    const elapsed = getElapsedCalendarParts(assignmentDate, now);
    const safeYears = Math.max(1, elapsed.years);
    return elapsed.months > 0 ? `${safeYears}y ${elapsed.months}mo` : `${safeYears}y`;
  }

  if (days > 30) {
    const elapsed = getElapsedCalendarParts(assignmentDate, now);
    const totalMonths = elapsed.years * 12 + elapsed.months;
    const safeMonths = Math.max(1, totalMonths);
    return elapsed.days > 0 ? `${safeMonths}mo ${elapsed.days}d` : `${safeMonths}mo`;
  }

  return `${days}d`;
};

const compareLastAssignmentDates = (dateA, dateB) => {
  const parsedA = parseDateOnlyToLocalDate(dateA);
  const parsedB = parseDateOnlyToLocalDate(dateB);

  if (!parsedA && !parsedB) return 0;
  if (!parsedA) return 1;
  if (!parsedB) return -1;

  return parsedA.getTime() - parsedB.getTime();
};

const Linguists = () => {
  usePageTitle("Linguists");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const navigate = useNavigate();

  const { setOverlayData, setOverlayName, showSnackbar } = useStateContext();

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("LINGUISTS_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("LINGUISTS_COLUMNS_VISIBLE");
    return saved ? JSON.parse(saved) : {};
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'
  const [overlayAnchorEl, setOverlayAnchorEl] = useState(null);

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("LINGUISTS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "LINGUISTS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  const [isLoading, setIsLoading] = useState(true);

  const [listofLinguists, setListOfLinguists] = useState([]);

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listofLanguages, setListOfLanguages] = useState("");
  const [listofLinguistStatuses, setListOfLinguistStatuses] = useState("");
  const [listofLanguageModes, setListofLanguageModes] = useState("");
  const [requirementDefinitions, setRequirementDefinitions] = useState([]);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const longPressTriggeredRef = useRef(false);
  const LONG_PRESS_MS = 500;

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for LinguistsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "LinguistsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "LinguistsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Name" -> "LINGUISTS_FILTER_NAME"
    const upperFieldName = fieldName.toUpperCase();
    return `LINGUISTS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const allFilterFields = useMemo(
    () => [
      "Name",
      "Email",
      "PhoneNumber",
      "Address",
      "Status",
      "LinguistLanguages.Rate",
      "LinguistLanguages.Languages",
      "MinimumHours",
      "LinguistLanguages.InterpretationTypes",
      "Active",
      ...requirementDefinitions
        .filter((definition) => definition?.SourceColumn)
        .map((definition) => definition.SourceColumn),
      "Certifications",
    ],
    [requirementDefinitions],
  );

  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};

    allFilterFields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [allFilterFields, loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    allFilterFields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [allFilterFields, getFilterStorageKey]);

  const LINGUISTS_FILTER_KEY = "LINGUISTS_FILTER_MODEL";

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(LINGUISTS_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      longPressTriggeredRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      longPressTriggeredRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = useCallback(() => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressNextClickRef.current = false;
  }, []);

  const handleMergeLinguists = useCallback(() => {
    if (selectionModel.length < 1) {
      showSnackbar("Select at least 1 linguist to merge.", theme, "error");
      return;
    }

    setOverlayData({
      Linguists: listofLinguists,
      SelectedLinguistIds: selectionModel,
    });

    setOverlayName(OVERLAY_NAMES.LINGUIST_MERGE);
  }, [
    selectionModel,
    showSnackbar,
    theme,
    setOverlayData,
    listofLinguists,
    setOverlayName,
  ]);

  const handleContextMenuEdit = () => {
    const secondData = {
      LinguistID: selectedRow,
      Linguist: listofLinguists.find((linguist) => linguist.id === selectedRow),
      Languages: listofLanguages,
      LinguistStatuses: listofLinguistStatuses,
      LanguageModes: listofLanguageModes,
      Action: OVERLAY_NAMES.LINGUIST_ACTION_EDIT,
    };

    setOverlayData(secondData);

    setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);

    setContextMenu(null);
  };

  const handleContextMenuCall = () => {
    const selectedLinguist = listofLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const phoneNumber = selectedLinguist.PhoneNumber;
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendEmail = () => {
    const selectedLinguist = listofLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const email = selectedLinguist.Email;
      if (email) {
        window.open(`mailto:${email}`, "_blank");
      } else {
        showSnackbar("No Email Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendSMS = () => {
    const selectedLinguist = listofLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const phoneNumber = selectedLinguist.PhoneNumber;
      if (phoneNumber) {
        window.location.href = `sms:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuOpenMaps = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Address) {
        const address = encodeURIComponent(linguist.Address);
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${address}`,
          "_blank",
        );
      } else {
        showSnackbar("No Address Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyEmail = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Email) {
        navigator.clipboard
          .writeText(linguist.Email)
          .then(() =>
            showSnackbar(
              "Email successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar("Failed to copy email to clipboard.", theme, "error"),
          );
      } else {
        showSnackbar("No Email Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyNumber = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.PhoneNumber) {
        navigator.clipboard
          .writeText(linguist.PhoneNumber)
          .then(() =>
            showSnackbar(
              "Phone number successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy phone number to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyAddress = () => {
    const linguist = listofLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Address) {
        navigator.clipboard
          .writeText(linguist.Address)
          .then(() =>
            showSnackbar(
              "Address successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy address to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar("No Address Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuAddTask = () => {
    const linguistData = listofLinguists.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Linguist",
        EntityID: selectedRow,
      },
      Linguist: {
        id: linguistData.id,
        Name: linguistData.Name,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  // Helper functions to check if data is available
  const getSelectedLinguist = () => {
    return listofLinguists.find((linguist) => linguist.id === selectedRow);
  };

  const hasPhoneNumber = () => {
    const linguist = getSelectedLinguist();
    return linguist?.PhoneNumber;
  };

  const hasEmail = () => {
    const linguist = getSelectedLinguist();
    return linguist?.Email;
  };

  const hasAddress = () => {
    const linguist = getSelectedLinguist();
    return linguist?.Address;
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const { refreshPage } = useStateContext();

  // Fetch linguists data
  const fetchLinguistsData = useCallback(() => {
    api
      .all([
        api.get("/linguists"),
        api.get("/languages"),
        api.get("/linguists/linguistStatuses"),
        api.get("/linguists/languageModes"),
        api.get("/linguists/requirements"),
      ])
      .then(
        api.spread(
          (
            linguistsResponse,
            languagesResponse,
            linguistStatusesResponse,
            languageModesResponse,
            requirementDefinitionsResponse,
          ) => {
            if (linguistsResponse !== null) {
              setListOfLinguists(
                (linguistsResponse.data || []).map(normalizeLinguistRequirements),
              );
            }

            if (languagesResponse !== null) {
              setListOfLanguages(languagesResponse.data);
            }

            if (linguistStatusesResponse !== null) {
              setListOfLinguistStatuses(linguistStatusesResponse.data);
            }

            if (languageModesResponse !== null) {
              setListofLanguageModes(languageModesResponse.data);
            }

            if (requirementDefinitionsResponse !== null) {
              setRequirementDefinitions(requirementDefinitionsResponse.data || []);
            }

            setIsLoading(false);
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsDataListsReady(true);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    fetchLinguistsData();
  }, [refreshPage, fetchLinguistsData]);

  useEffect(() => {
    setSelectionModel((prev) =>
      prev.filter((selectedId) =>
        (listofLinguists || []).some((linguist) => linguist.id === selectedId),
      ),
    );
  }, [listofLinguists]);

  // Listen for linguist updates from other members
  const handleLinguistsListRefetch = useCallback(
    (updatedBy) => {
      // Store update info for snackbar (separate effect will show it)
      setLastUpdate(updatedBy);

      // Refetch the data
      fetchLinguistsData();
    },
    [fetchLinguistsData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Linguist updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "LinguistsList",
    recordId: "all",
    onUpdate: handleLinguistsListRefetch,
  });

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      setOverlayData({
        Languages: listofLanguages,
        LinguistStatuses: listofLinguistStatuses,
        LanguageModes: listofLanguageModes,
        Action: OVERLAY_NAMES.LINGUIST_ACTION_NEW,
      });

      setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listofLanguages,
    listofLinguistStatuses,
    listofLanguageModes,
    setOverlayData,
    setOverlayName,
  ]);

  const handleSortModelChange = () => {
    // setSortModel is intentionally controlled through column filter controls
  };

  const getRequirementColumnWidth = (headerName) => {
    const safeHeaderName = String(headerName || "").trim();
    if (safeHeaderName.length <= 12) {
      return 120;
    }

    return safeHeaderName.length * 12;
  };

  const getRequirementDisplayValue = (row, sourceColumn) => {
    const mapValue = normalizeRequirementValue(row?.RequirementMap?.[sourceColumn]);
    if (mapValue === true) return "Yes";
    if (mapValue === false) return "No";

    const directValue = normalizeRequirementValue(row?.[sourceColumn]);
    if (directValue === true) return "Yes";
    if (directValue === false) return "No";

    return "N/A";
  };

  const renderColumnHeader = (params) => (
    <DataGridHeader headerName={params.colDef.headerName}>
      <ColumnFilter
        data={filterDataByField[params.field] || []}
        field={params.field}
        valueGetter={params.colDef.valueGetter}
        selectedValues={columnFilters[params.field] || []}
        onFilterChange={(values) =>
          handleColumnFilterChangeWithStorage(params.field, values)
        }
        onSortChange={(field, direction) => {
          const newSortModel = direction ? [{ field, sort: direction }] : [];
          setSortModel(newSortModel);
          handleSortModelChange(newSortModel);
        }}
        currentSort={sortModel.find((s) => s.field === params.field)?.sort || null}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
      />
    </DataGridHeader>
  );

  const requirementColumns = requirementDefinitions
    .filter((definition) => definition?.SourceColumn)
    .map((definition) => {
      const sourceColumn = definition.SourceColumn;
      const headerName = definition.Name || sourceColumn;

      return {
        field: sourceColumn,
        headerName,
        width: getRequirementColumnWidth(headerName),
        valueGetter: (_, row) => getRequirementDisplayValue(row, sourceColumn),
        renderCell: (params) => {
          const value = params.value;
          const color =
            value === "Yes"
              ? theme.palette.primary.main
              : theme.palette.error.main;
          return <span style={{ color }}>{value}</span>;
        },
        renderHeader: renderColumnHeader,
      };
    });

  const columns = [
    {
      field: "Name",
      headerName: "Name",
      minWidth: 170,
      flex: 1.7,
      renderHeader: renderColumnHeader,
    },
    {
      field: "PhoneNumber",
      headerName: "Phone Number",
      valueGetter: (_, row) => normalizePhoneNumber(row.PhoneNumber),
      minWidth: 160,
      flex: 1.6,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Email",
      headerName: "Email",
      minWidth: 180,
      flex: 1.8,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Address",
      headerName: "Address",
      minWidth: 350,
      flex: 3.5,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Status",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      renderHeader: renderColumnHeader,
    },
    {
      field: "lastAssignmentDate",
      headerName: "Last Assignment",
      valueGetter: (_, row) => formatLastAssignmentAge(row.lastAssignmentDate),
      sortComparator: (_, __, cellParamsA, cellParamsB) =>
        compareLastAssignmentDates(
          cellParamsA.row.lastAssignmentDate,
          cellParamsB.row.lastAssignmentDate,
        ),
      minWidth: 140,
      flex: 1.2,
      renderHeader: renderColumnHeader,
    },
    {
      field: "LinguistLanguages.Rate",
      headerName: "Rate",
      sortComparator: numericComparator,
      valueGetter: (_, row) => {
        const data = row.LinguistLanguages;
        if (!data || data.length === 0) {
          return "N/A";
        }

        const uniqueItems = new Set(
          data
            .filter((item) => item.Rate !== null)
            .map((item) => "$" + item.Rate),
        );

        return Array.from(uniqueItems).join(" | ") || "N/A";
      },
      minWidth: 85,
      flex: 0.85,
      renderHeader: renderColumnHeader,
    },
    {
      field: "LinguistLanguages.Languages",
      headerName: "Languages",
      valueGetter: (_, row) => {
        const data = row.LinguistLanguages;
        if (!data || data.length === 0) {
          return "N/A";
        }

        const uniqueItems = new Set(
          data
            .filter(
              (item) =>
                item.Language.Name !== null && item.Language.Name !== "",
            )
            .map((item) => item.Language.Name),
        );

        return Array.from(uniqueItems).join(" | ") || "N/A";
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: renderColumnHeader,
    },
    {
      field: "MinimumHours",
      headerName: "Minimum Hours",
      valueGetter: (_, row) => row.MinimumHours || "N/A",
      minWidth: 165,
      flex: 1.65,
      renderHeader: renderColumnHeader,
    },
    {
      field: "LinguistLanguages.InterpretationTypes",
      headerName: "Interpretation Types",
      valueGetter: (_, row) => {
        const availabilityFlags = new Set();

        if (row.IsAvailablePhone) {
          availabilityFlags.add("P");
        }
        if (row.IsAvailableVirtual) {
          availabilityFlags.add("V");
        }
        if (row.IsAvailableInPerson) {
          availabilityFlags.add("I");
        }

        return Array.from(availabilityFlags).join(", ") || "N/A";
      },
      minWidth: 200,
      flex: 2,
      renderHeader: renderColumnHeader,
    },
    {
      field: "Active",
      headerName: "Active",
      valueGetter: (_, row) => {
        return row.Active === true
          ? "Yes"
          : row.Active === false
            ? "No"
            : "N/A";
      },
      renderCell: (params) => {
        const value = params.value;
        const color =
          value === "Yes"
            ? theme.palette.primary.main
            : theme.palette.error.main;
        return <span style={{ color }}>{value}</span>;
      },
      minWidth: 100,
      flex: 1,
      renderHeader: renderColumnHeader,
    },
    ...requirementColumns,
    {
      field: "Certifications",
      headerName: "Certification",
      valueGetter: (_, row) => {
        const data = row.Certifications;
        if (!data || data.length === 0) {
          return "N/A";
        }

        return data;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: renderColumnHeader,
    },
  ];

  // Filter columns based on visibility preferences
  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState(getStoredFilterModel);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listofLinguists || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Filter columns configuration for cross-filtering
  const filterColumns = columns.map((column) => ({
    field: column.field,
    valueGetter: (row) => {
      if (typeof column.valueGetter === "function") {
        return column.valueGetter(undefined, row);
      }

      return row?.[column.field] || "";
    },
  }));

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listofLinguists || listofLinguists.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listofLinguists, loadAllColumnFilters]);

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();

    const emptyFilterModel = { items: [] };
    setFilterModel(emptyFilterModel);
    localStorage.setItem(
      LINGUISTS_FILTER_KEY,
      JSON.stringify(emptyFilterModel),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Quick filter handling
  const handleFilterModelChange = (newFilterModel) => {
    localStorage.setItem(LINGUISTS_FILTER_KEY, JSON.stringify(newFilterModel));
    setFilterModel(newFilterModel);
  };

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Cross-filtering data calculation
  const filterDataByField = useMemo(() => {
    const filterData = {};

    filterColumns.forEach((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        filterData[column.field] = listofLinguists || [];
        return;
      }

      filterData[column.field] = (listofLinguists || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });

    return filterData;
  }, [columnFilters, listofLinguists, filterColumns]);

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export", {
                  columnFilteredData,
                  listofLinguists,
                });
                showSnackbar(
                  "No linguists data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              const csvRows = columnFilteredData
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            value = row[col.field];
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");
              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `linguists-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export", {
                  columnFilteredData,
                  listofLinguists,
                });
                showSnackbar(
                  "No linguists data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Create workbook and worksheet
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Linguists");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              columnFilteredData.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      value = row[col.field];
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `linguists-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, theme, isMobileToolbar }) => {
      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const openGenerateSelected = () => {
        if (!selectionModel.length) return;

        const selectedLinguists = (listofLinguists || []).filter((linguist) =>
          selectionModel.includes(linguist.id),
        );

        if (!selectedLinguists.length) return;

        setOverlayData({
          Linguists: selectedLinguists,
          LinguistIDs: selectedLinguists.map((linguist) => linguist.id),
          Linguist: selectedLinguists[0],
          LinguistID: selectedLinguists[0]?.id,
        });
        setOverlayName(OVERLAY_NAMES.LINGUIST_DOCUMENT_GENERATION);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {selectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={openGenerateSelected}
                disabled={selectionModel.length === 0}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Generate Documents
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleMergeLinguists}
                disabled={selectionModel.length === 0}
                startIcon={<MergeRoundedIcon />}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Merge Linguists
              </Button>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {hasAnyActiveFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && hasAnyActiveFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobileToolbar} />

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: theme.palette.grey.main,
                  borderColor: theme.palette.divider,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openDialog({
                    title: "Export Data",
                    theme: theme,
                    customContent: <ExportDialogContent />,
                  });
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    selectionEnabled,
    selectionModel,
    listofLinguists,
    handleCancelSelection,
    handleMergeLinguists,
    setOverlayData,
    setOverlayName,
    openDialog,
  ]);

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ pr: 1, gap: 2 }}
          >
            {!isMobile ? (
              <>
                <Button
                  onClick={() => {
                    setOverlayData({ mode: "all" });
                    setOverlayName(OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS);
                  }}
                  startIcon={<FileCopyRoundedIcon />}
                  sx={{
                    width: "220px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>All Documents</Box>
                </Button>

                <Button
                  onClick={() => {
                    setPendingCreateNew(true);
                  }}
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>Add Linguist</Box>
                </Button>
              </>
            ) : (
              <>
                <Tooltip title="All Documents">
                  <IconButton
                    onClick={() => {
                      setOverlayData({ mode: "all" });
                      setOverlayName(OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS);
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      borderRadius: "12px",
                      border: "none",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <FileCopyRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Button
                  onClick={() => {
                    setPendingCreateNew(true);
                  }}
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <AddRoundedIcon />
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          mb: 1,
          mx: 1,
          mt: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: theme.shadows[3],
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          columns={filteredColumns}
          apiRef={apiRef}
          density={density}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event, details) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            if (selectionEnabled) {
              if (suppressNextClickRef.current) {
                event.defaultMuiPrevented = true;
                suppressNextClickRef.current = false;
                return;
              }
              return;
            }

            if (longPressTriggeredRef.current) {
              return;
            }

            setSelectionModel([params.id]);
          }}
          onRowDoubleClick={(params, event, details) => {
            if (openFilter || selectionEnabled || longPressTriggeredRef.current) {
              return;
            }

            const data = {
              Linguist: params.row,
              LinguistID: params.row.id,
            };

            setOverlayData(data);
            navigate(`/linguists/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "✘" || params.value === "N/A"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              showQuickFilter: true,
              hasAnyActiveFilters,
              onClear: handleToolbarClear,
              theme,
              isMobileToolbar: isMobile,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            height: "100%", // Make DataGrid take full height of container
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={handleContextMenuAddTask}>
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Add Task</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCall}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <CallRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Call</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendSMS}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <SmsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send SMS</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuOpenMaps}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <LocationOnRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Open Maps</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCopyEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyNumber}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Number</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyAddress}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Address</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            overlayAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Linguists;
